// src/axios.js
import axios from "axios";
import config from "../configuration.json";

const ENV_BASE =
  (typeof process !== "undefined" && process?.env?.REACT_APP_API_BASE_URL) ||
  "";

export const API_BASE_URL =
  String(ENV_BASE || config?.apiBaseUrl || "").trim() || window.location.origin;

export const ENDPOINTS = {
  login: "/nba/user/login/",
  register: "/nba/user/applicant/register/",
  forgotRequest: "/nba/user/forgot-password/request/",
  refreshToken: "/user/refresh-token/",
};

/* ── Session-expired event bus ── */
const SESSION_EXPIRED_EVENT = "nba:session-expired";
let _sessionExpiredFired = false;
let _logoutInProgress = false;

export function onSessionExpired(callback) {
  const handler = () => callback();
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
}

function fireSessionExpired() {
  if (_sessionExpiredFired) return;
  _sessionExpiredFired = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  // reset after 5s so it can fire again if user doesn't act
  setTimeout(() => { _sessionExpiredFired = false; }, 5000);
}

/**
 * forceLogout — immediately clear all auth state and redirect to login.
 * Called on ANY unrecoverable 401/403. Safe to call multiple times
 * (only the first call actually redirects).
 */
export function forceLogout() {
  if (_logoutInProgress) return;
  _logoutInProgress = true;

  stopTokenAutoRefresh();

  // Clear all auth storage
  try {
    sessionStorage.clear();
    localStorage.removeItem("userInfo");
    // Clear auth cookies
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
    });
  } catch { /* ignore */ }

  // Fire event for any listeners (SessionExpiredModal etc.)
  fireSessionExpired();

  // Immediate redirect — use window.location so it works outside React
  // Small delay to allow any in-flight UI updates
  setTimeout(() => {
  _logoutInProgress = false;

  const base = process.env.PUBLIC_URL || "/";

  if (!window.location.pathname.includes("/login")) {
    window.location.href = `${base}/login`;
  }
}, 100);
}

/* ── Friendly, situation-aware error messages ────────────────────────────────
   The user must NEVER see raw technical errors — stack traces, "Internal Server
   Error", Keycloak codes (invalid_grant…), SQL, HTML error pages or JSON dumps.

   • humanizeServerMessage(raw)  → a message safe to show, or '' if it's technical
   • getFriendlyErrorMessage(err, context) → clear guidance for any failure, tuned
       by an optional context: 'login' | 'register' | 'forgotPassword' |
       'changePassword' | 'resetPassword' | 'saveForm' | 'submit' | 'payment'
   • extractFieldErrors(err)     → { field: message } when the API returns a
       field-level validation list
   Accepts an axios error, a thrown Error, or a plain { status, data } object so
   fetch-based callers can use it too.
──────────────────────────────────────────────────────────────────────────── */

// OAuth / Keycloak error codes → human copy (never show the raw code).
const OAUTH_MESSAGES = {
  invalid_grant:           "Your session or credentials are no longer valid. Please sign in again.",
  invalid_token:           "Your session has expired. Please sign in again.",
  unauthorized_client:     "This action isn’t permitted for your account.",
  access_denied:           "You don’t have permission to perform this action.",
  invalid_request:         "Some required information was missing. Please try again.",
  invalid_client:          "We couldn’t verify the request. Please try again.",
  server_error:            "Our server ran into a problem. Please try again shortly.",
  temporarily_unavailable: "The service is temporarily unavailable. Please try again shortly.",
};

// Server text that must never be surfaced verbatim (technical / infra / dumps).
/* Server text an applicant must never be shown.
   Three additions worth naming, all seen in production on this portal:
     • "An unexpected error occurred: TransactionSystemException…" — arrives on
       a 200 with success:false, so no status code filters it out. It tells the
       applicant nothing and reads as though their data were rejected.
     • any …Exception class name, whether or not a package path came with it
       (the message is often truncated before the package).
     • "internal error", the same thing in fewer words. */
const TECHNICAL_MESSAGE_RE = /\b(internal server error|internal error|(?:an\s+)?unexpected error|service unavailable|bad gateway|gateway time-?out|stack ?trace|null ?pointer(exception)?|classcast|numberformat|illegalstate|illegalargument|sqlexception|sql syntax|ora-\d|econnrefused|econnreset|etimedout|socket hang ?up|proxy error|whitelabel|keycloak|invalid_grant|invalid_token|unauthorized_client|realmresource)\b|\b\w+exception\b|<!doctype|<html|\bat\s+[\w$.]+\.[\w$]+\(|\b(?:com|org|net|io|javax?)\.[\w.$]{3,}|\{\s*"/i;

const clampMsg = (s, max = 240) => {
  const t = String(s).replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trim()}…` : t;
};

export function humanizeServerMessage(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const oauthKey = Object.keys(OAUTH_MESSAGES).find((k) =>
    new RegExp(`(^|[^a-z])${k}([^a-z]|$)`, "i").test(s)
  );
  if (oauthKey) return OAUTH_MESSAGES[oauthKey];
  if (TECHNICAL_MESSAGE_RE.test(s)) return "";
  if (/^[A-Z0-9_.-]{4,}$/.test(s)) return "";    // bare codes e.g. INTERNAL_ERROR
  if (s.length > 300) return "";                  // suspiciously long ⇒ probably a dump
  return clampMsg(s);
}

/* ── Spring/Hibernate validation decoding ───────────────────────────────────
   Some backends bubble a raw MethodArgumentNotValidException up in `message`
   (a giant Java dump). It is filtered out by humanizeServerMessage as unsafe —
   but it actually names the failing fields and constraints, so we decode it into
   precise, per-field guidance instead of a generic "server error". */

// Common backend field keys → { form field name, human label }.
const FIELD_META = {
  userfirstname:    { key: "firstName",    label: "First name" },
  firstname:        { key: "firstName",    label: "First name" },
  userfullname:     { key: "firstName",    label: "Name" },
  userlastname:     { key: "lastName",     label: "Last name" },
  lastname:         { key: "lastName",     label: "Last name" },
  useremailid:      { key: "email",        label: "Email" },
  emailid:          { key: "email",        label: "Email" },
  username:         { key: "email",        label: "Email" },
  email:            { key: "email",        label: "Email" },
  usermobilenumber: { key: "mobileNumber", label: "Mobile number" },
  mobilenumber:     { key: "mobileNumber", label: "Mobile number" },
  userpassword:     { key: "password",     label: "Password" },
  password:         { key: "password",     label: "Password" },
};
const prettifyField = (f) =>
  String(f || "").replace(/^user/i, "").replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase()) || "This field";
const fieldMeta = (f) => FIELD_META[String(f || "").toLowerCase()] || { key: String(f || ""), label: prettifyField(f) };

// One parsed Spring field error → a clear, human sentence.
function describeConstraint({ field, constraint, min, max }) {
  const { label } = fieldMeta(field);
  const c = String(constraint || "").toLowerCase();
  if (c === "size") {
    if (min && max) return `${label} must be between ${min} and ${max} characters.`;
    if (max) return `${label} must be at most ${max} characters.`;
    if (min) return `${label} must be at least ${min} characters.`;
    return `${label} has an invalid length.`;
  }
  if (c === "notnull" || c === "notblank" || c === "notempty") return `${label} is required.`;
  if (c === "email") return "Please enter a valid email address.";
  if (c === "pattern") return `${label} is not in the expected format.`;
  if (c === "min") return `${label} must be at least ${min ?? ""}.`.replace(" .", ".");
  if (c === "max") return `${label} must be at most ${max ?? ""}.`.replace(" .", ".");
  return `${label} is invalid.`;
}

// Extract Spring field errors from a raw exception dump.
// → [{ field, constraint, min, max, rejected }]
export function parseSpringValidationErrors(raw) {
  const s = String(raw || "");
  if (!/Field error in object/i.test(s)) return [];
  const out = [];
  const re = /on field '([^']+)':\s*rejected value \[([^\]]*)\];\s*codes \[([A-Za-z]+)/g;
  let m;
  while ((m = re.exec(s))) {
    const field = m[1], rejected = m[2], constraint = m[3];
    let min, max;
    const am = new RegExp(`default message \\[${field}\\],(\\d+),(\\d+)\\]`).exec(s);
    if (am) { max = Number(am[1]); min = Number(am[2]); }
    out.push({ field, rejected, constraint, min, max });
  }
  return out;
}

// A single friendly sentence summarising a Spring validation dump, or '' if none.
export function summarizeSpringValidation(raw) {
  const errs = parseSpringValidationErrors(raw);
  if (!errs.length) return "";
  return [...new Set(errs.map(describeConstraint))].join(" ");
}

// Per-flow copy. Keys are HTTP statuses (or 'default'); missing keys fall back
// to the generic status copy in getFriendlyErrorMessage.
const CONTEXT_COPY = {
  login: {
    400: "The email or password you entered is incorrect. Please try again.",
    401: "The email or password you entered is incorrect. Please try again.",
    404: "We couldn’t find an account with those details. Please check your email or register first.",
    default: "We couldn’t sign you in. Please check your details and try again.",
  },
  register: {
    409: "An account with this email already exists. Try signing in instead.",
    422: "Some details don’t look right. Please review the highlighted fields and try again.",
    default: "We couldn’t complete your registration. Please review your details and try again.",
  },
  forgotPassword: {
    404: "If an account exists for this email, a reset link is on its way. Please check your inbox and spam folder.",
    default: "We couldn’t send the reset link right now. Please try again in a moment.",
  },
  changePassword: {
    400: "Your current password is incorrect, or the new password doesn’t meet the requirements. Please try again.",
    401: "For your security, please sign in again to change your password.",
    default: "We couldn’t update your password. Please try again.",
  },
  resetPassword: {
    400: "This reset link is invalid, or the new password doesn’t meet the requirements. Please request a new link.",
    404: "This reset link is no longer valid. Please request a new one.",
    410: "This reset link has expired. Please request a new one.",
    default: "We couldn’t reset your password — the link may have expired. Please request a new one.",
  },
  /* The 500 lines carry no "please try again" of their own: for a server-side
     failure that sentence comes from SUPPORT_LINE, which is appended to them
     (see withSupport). Two of them read as a stutter. */
  saveForm: {
    default: "We couldn’t save your draft just now. Your entries are still here.",
    500: "We couldn’t save your draft due to a server issue. Your entries are safe and still on this page.",
  },
  submit: {
    default: "We couldn’t submit your application just now. Nothing you filled in has been lost.",
    500: "We couldn’t submit your application due to a server issue. Nothing you filled in has been lost.",
  },
  payment: {
    default: "We couldn’t start the payment just now. No money has been deducted.",
    500: "The payment service is briefly unavailable. No money has been deducted.",
  },
};

/* Where an applicant is meant to turn when the failure is NOT theirs to fix.
   Appended to server-side and unexplained failures only — never to a validation
   message, where the applicant can simply correct the field and move on. */
const SUPPORT_LINE =
  "Please try once more; if it keeps happening, contact the NBA administration team. Sorry for the inconvenience.";
const withSupport = (msg) => {
  const s = String(msg || "").trim();
  if (!s) return SUPPORT_LINE;
  if (/nba administration team/i.test(s)) return s;   // already says it
  return `${s.replace(/\s+$/, "")} ${SUPPORT_LINE}`;
};

export function getFriendlyErrorMessage(error, context = "") {
  const status = error?.response?.status ?? error?.status;
  const data =
    error?.response?.data ?? error?.data ?? (error && typeof error === "object" ? error : {});
  const rawServerMsg =
    data?.responseMessage ||
    data?.message ||
    data?.error_description ||
    data?.error ||
    (Array.isArray(data?.responseList) ? data.responseList[0]?.listResponseMessage : "") ||
    data?.responseObject?.message ||
    (typeof data === "string" ? data : "") ||
    "";
  // A raw Spring validation dump actually names the bad fields — decode it into
  // precise guidance rather than falling through to a generic server error.
  const springSummary = summarizeSpringValidation(rawServerMsg);
  if (springSummary) return springSummary;

  const safe = humanizeServerMessage(rawServerMsg);
  const ctx = CONTEXT_COPY[context] || {};
  const pick = (code, fallback) => ctx[code] || fallback;

  // No HTTP response at all → network / timeout / thrown Error.
  const hasResponse = !!(error?.response || error?.status || error?.data);
  if (!hasResponse) {
    if (error?.code === "ECONNABORTED" || /timeout|timed out/i.test(String(error?.message || ""))) {
      return "This is taking longer than usual. Please check your connection and try again.";
    }
    if (/network|failed to fetch|load failed/i.test(String(error?.message || ""))) {
      return "We couldn’t reach the server. Please check your internet connection and try again.";
    }
    const m = humanizeServerMessage(error?.message);
    return m || "We couldn’t reach the server. Please check your connection and try again.";
  }

  switch (status) {
    case 400:
    case 422:
      return safe || pick(400, "Some details don’t look right. Please review the highlighted fields and try again.");
    case 401:
      return pick(401, safe || "Your session has ended. Please sign in again to continue.");
    case 403:
      return pick(403, "You don’t have permission to do this. If you believe this is a mistake, please contact the NBA administration team.");
    case 404:
      return pick(404, safe || "We couldn’t find what you were looking for. It may have been moved or removed.");
    case 408:
      return "The request timed out. Please try again.";
    case 409:
      return safe || pick(409, "This conflicts with existing information. Please review and try again.");
    case 423:
      return "Your account is locked. Please contact the NBA administration team to unlock it.";
    case 429:
      return "You’ve tried several times in quick succession. Please wait a moment and try again.";
    /* 5xx and anything unexplained are OURS, not the applicant's: they carry the
       support line, because no amount of re-reading the form will fix them.
       The server's own words are deliberately NOT used here — a 500 body is a
       Java exception ("An unexpected error occurred: TransactionSystem…"), which
       tells an applicant nothing and reads as if their data were at fault. */
    case 500:
    case 502:
    case 503:
      return withSupport(pick(500, "Our server ran into a problem. Your information is safe."));
    case 504:
      return withSupport("The server took too long to respond.");
    default:
      if (typeof status === "number" && status >= 500)
        return withSupport(pick(500, "Our server ran into a problem. Your information is safe."));
      return safe || withSupport(pick("default", "Something went wrong."));
  }
}

// Pull { field: message } out of a server field-validation payload, if present.
export function extractFieldErrors(error) {
  const data = error?.response?.data ?? error?.data ?? {};
  const out = {};
  const list = Array.isArray(data?.responseList)
    ? data.responseList
    : Array.isArray(data?.errors)
    ? data.errors
    : Array.isArray(data?.fieldErrors)
    ? data.fieldErrors
    : [];
  list.forEach((it) => {
    const field = it?.field || it?.fieldName || it?.property;
    const msg = humanizeServerMessage(it?.listResponseMessage || it?.message || it?.defaultMessage);
    if (field) { const { key } = fieldMeta(field); if (key && msg) out[key] = msg; }
  });
  // Also decode a raw Spring validation dump embedded in the message.
  const rawMsg = data?.message || data?.responseMessage || (typeof data === "string" ? data : "");
  parseSpringValidationErrors(rawMsg).forEach((e) => {
    const { key } = fieldMeta(e.field);
    if (key && !out[key]) out[key] = describeConstraint(e);
  });
  return out;
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    "Pragma": "no-cache",
    "Expires": "0",
  },
});

const getAccessToken = () => {
  return sessionStorage.getItem("access-token") || "";
};

const getRefreshToken = () => {
  return sessionStorage.getItem("refresh-token") || "";
};

export const setTokens = (access_token, refresh_token) => {
  if (access_token) sessionStorage.setItem("access-token", access_token);
  if (refresh_token) sessionStorage.setItem("refresh-token", refresh_token);
  // Keep the proactive background refresh running while a session exists.
  if (access_token) startTokenAutoRefresh();
};

/* ── Token refresh (shared by interceptor + proactive ensureFreshToken) ── */

// Decode the `exp` (epoch seconds) claim from a JWT without verifying it.
const getTokenExp = (token) => {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
};

// Normalises the various refresh-response shapes the backend may return.
// The auth service nests tokens under `responseObject.tokenInfo` (same shape as
// login), but older/alternate responses put them flat — handle both so a
// successful refresh is never mis-read as "no token" (which would force logout).
const extractTokens = (data) => {
  let ro = data?.responseObject ?? data ?? {};
  if (typeof ro === "string") {
    try { ro = JSON.parse(ro); } catch { ro = {}; }
  }
  const src = ro && typeof ro === "object" && ro.tokenInfo ? ro.tokenInfo : ro;
  const access_token = src?.access_token || src?.accessToken || "";
  const refresh_token = src?.refresh_token || src?.refreshToken || "";
  return { access_token, refresh_token };
};

// In-flight refresh promise so concurrent callers share a single request.
let _refreshPromise = null;

const performRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token available");

  const res = await axios.post(
    `${API_BASE_URL}${ENDPOINTS.refreshToken}`,
    { userRefreshToken: refreshToken },
    { headers: { "Content-Type": "application/json" }, timeout: 20000 }
  );

  const { access_token, refresh_token } = extractTokens(res?.data);
  if (!access_token) throw new Error("No access token in refresh response");

  setTokens(access_token, refresh_token);
  return access_token;
};

// Refresh the access token, de-duplicating concurrent calls.
export const refreshAccessToken = () => {
  if (!_refreshPromise) {
    _refreshPromise = performRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
};

/**
 * ensureFreshToken — proactively refresh the access token if it is missing
 * or will expire within `minValiditySeconds`. Mirrors keycloak.updateToken().
 *
 * Call this right before a slow/important request (e.g. saving a long form):
 *   await ensureFreshToken(30);   // refresh if < 30s of validity left
 *
 * Resolves `true` if a refresh happened, `false` if the token was still valid.
 * Rejects only if a refresh was required but failed — the caller should then
 * treat the session as expired (forceLogout()).
 */
export const ensureFreshToken = async (minValiditySeconds = 30) => {
  const token = getAccessToken();
  const exp = token ? getTokenExp(token) : 0;
  const now = Date.now() / 1000;

  // Still comfortably valid — nothing to do.
  if (token && exp && exp - now > minValiditySeconds) return false;

  await refreshAccessToken();
  return true;
};

/* ── Background auto-refresh ──────────────────────────────────────────────────
   The refresh token has the SAME 30-min lifetime as the access token, so once
   the access token expires the refresh token is dead too and the session cannot
   recover. The request interceptor only refreshes when an API call happens — but
   a user can fill a long form for 30 min WITHOUT any API call, letting both
   tokens expire and getting logged out on their next save.

   This timer keeps an ACTIVE user signed in by refreshing a few minutes BEFORE
   expiry (while the refresh token is still valid, which rolls the 30-min window).
   It is gated on recent user activity, so a genuinely idle session still expires.
────────────────────────────────────────────────────────────────────────────── */
const AUTO_REFRESH_CHECK_MS  = 60 * 1000;      // re-check every minute
const AUTO_REFRESH_BEFORE_S  = 5 * 60;         // refresh when < 5 min validity left
const MAX_IDLE_MS            = 25 * 60 * 1000;  // stop keeping alive after 25 min idle
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "wheel"];

let _autoRefreshTimer = null;
let _lastActivityTs = Date.now();
const _markActivity = () => { _lastActivityTs = Date.now(); };

export function startTokenAutoRefresh() {
  if (_autoRefreshTimer || typeof window === "undefined") return;
  _lastActivityTs = Date.now();
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, _markActivity, { passive: true }));
  _autoRefreshTimer = setInterval(() => {
    const token = getAccessToken();
    if (!token) return;
    const exp = getTokenExp(token);
    if (!exp) return;
    // Let a long-idle session expire naturally (respects the server idle timeout).
    if (Date.now() - _lastActivityTs > MAX_IDLE_MS) return;
    const now = Date.now() / 1000;
    if (exp - now <= AUTO_REFRESH_BEFORE_S) {
      // If this fails, the next request's 401 path force-logs-out cleanly.
      refreshAccessToken().catch(() => {});
    }
  }, AUTO_REFRESH_CHECK_MS);
}

export function stopTokenAutoRefresh() {
  if (_autoRefreshTimer) { clearInterval(_autoRefreshTimer); _autoRefreshTimer = null; }
  if (typeof window !== "undefined") {
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, _markActivity));
  }
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url || "";
    const isAuthEndpoint =
      url.includes("/login") ||
      url.includes("/register") ||
      url.includes("/forgot-password") ||
      url.includes("/refresh-token");

    // Proactively refresh a near-expired token before any authenticated call
    // (mirrors keycloak.updateToken). This prevents slow actions like saving a
    // long form from failing — and force-logging-out — mid-request.
    if (!isAuthEndpoint) {
      const current = getAccessToken();
      if (current) {
        const exp = getTokenExp(current);
        const now = Date.now() / 1000;
        if (exp && exp - now <= 120) {
          // If refresh fails, let the request proceed; the response
          // interceptor's 401 handling will force logout cleanly.
          try { await refreshAccessToken(); } catch { /* handled on 401 */ }
        }
      }
    }

    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    return config;
  },
  (error) => {
    console.error(`API ERROR :: ${error}`);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // Skip auth handling for login/register/forgot-password endpoints
    const url = originalRequest.url || "";
    const isAuthEndpoint =
      url.includes("/login") ||
      url.includes("/register") ||
      url.includes("/forgot-password") ||
      url.includes("/refresh-token");

    // 401 Unauthorized — try refresh once, then force logout
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const access_token = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout immediately
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // 401 after retry already attempted — force logout
    if (status === 401 && originalRequest._retry && !isAuthEndpoint) {
      forceLogout();
      return Promise.reject(error);
    }

    // 403 Forbidden — force logout (session/permission issue)
    if (status === 403 && !isAuthEndpoint) {
      forceLogout();
    }

    return Promise.reject(error);
  }
);

// Resume the background refresh if the page is reloaded while already signed in.
if (typeof window !== "undefined" && getAccessToken()) {
  startTokenAutoRefresh();
}

export default axiosInstance;
